import React, { useState } from 'react';
import { Box } from '@mui/material';
import { uploadImage } from '../../../services/imageUploaderService';

import { useParams, useNavigate } from 'react-router-dom';
import { useGetCompaniesQuery } from '../../../api/companyApi';
import { useToast } from '../../../hooks/useToast';
import { useGetBranchesByCompanyIdQuery } from '../../../api/branchApi';
import { User } from '../../../interfaces/entities/user';
import { EmployeeForm } from './employeeform';
import { useGetUserByIdQuery, useUpdateUserMutation } from '../../../api/userApi';
import { useLoader } from '../../../hooks/useLoader';
import { getUserImagePath } from '../../../services/storageService';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';

export const EmployeeEditContainer: React.FC = () => {
    const { companyId, userId } = useParams<{
        companyId: string;
        branchId: string;
        userId: string;
    }>(); // Mark id as optional

    const [employeeImage, setEmployeeImage] = useState<File | null>(null);
    const { currentUserIsOwnerOf } = useAuthenticatedUser();

    const navigate = useNavigate();

    const { data: companies = [], isLoading: loadingCompanies } = useGetCompaniesQuery();
    const { data: branches = [], isLoading: loadingBranches } = useGetBranchesByCompanyIdQuery(companyId ?? '');
    const { data: employee, isLoading: isLoadingEmployee } = useGetUserByIdQuery(userId ?? '');
    const [updateUser, updateProcess] = useUpdateUserMutation();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    // Handle form submission
    const handleFormSubmit = async (values: User) => {
        try {
            showLoader();

            // Step 1: Prepare branch data 
            const employeeData: User = { ...values };

            // Step 3: Upload image with ID as the file name (id.jpg)
            if (employeeImage) {
                const imageUrl = await uploadImage( getUserImagePath(employeeData.companyId, employeeData.id), employeeImage);
                employeeData.image = imageUrl;
            }

            await updateUser({ id: employeeData.id, data: employeeData }).unwrap();
            showSuccess("Employee updated successfully!");

            navigate(-1); // Navigate back to previous screen

        } catch (error: any) {
            console.error('Error while submitting form:', error);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };

    const handleImageChange = (file: File | null) => {
        setEmployeeImage(file);
    };

    const companyOptions = companies.map((company) => ({
        id: company.id,
        label: company.name,
    }));

    const branchOptions = branches.map((company) => ({
        id: company.id,
        label: company.name,
    }));

    if (isLoadingEmployee) {
        return <div>Loading employee details</div>
    }

    if (!employee) {
        return <div>Employee Not Found</div>
    }
    
    if (!currentUserIsOwnerOf(companyId)) return <div>Unauthorized</div>;

    return (
        <Box>
            <EmployeeForm
                title='Update Employee Details'
                initialValues={employee}
                onSubmit={handleFormSubmit}
                companyOptions={companyOptions}
                branchOptions={branchOptions}
                onImageChange={handleImageChange}
                isSubmitting={updateProcess.isLoading || isLoadingEmployee || loadingBranches || loadingCompanies}
                onCancel={() => navigate(-1)}
            />
        </Box>
    );
};
